import { useCallback } from 'react'

import { useForm, y } from '@circlefin/form'
import { UsersForms } from '@features/users/forms'
import { GraphQLErrorBoundary } from '@shared/components/errors'
import { useCompleteInviteMutation } from '@shared/graphql'
import useTranslation from 'next-translate/useTranslation'

import type { SignInCredential, UserActivation } from '@shared/graphql'

const schema = y.object({
  firstName: y.string().required(),
  lastName: y.string().required(),
  email: y.string().required().email(),
  password: UsersForms.Input.generatePasswordSchema('email'),
})

export type ActivateUserFormNewValues = y.InferType<typeof schema>

export interface ActivateUserFormNewProps
  extends Pick<
    UserActivation,
    'firstName' | 'lastName' | 'email' | 'stateToken'
  > {
  /**
   * Callback on form submit.
   */
  onSubmit: (credential?: SignInCredential | null) => Promise<void>
  /**
   * State token.
   */
  stateToken: string
}

export const ActivateUserFormNew: React.FC<ActivateUserFormNewProps> = ({
  email,
  stateToken,
  firstName,
  lastName,
  onSubmit,
}) => {
  const { t } = useTranslation('onboard/common')
  const [completeInvite, completeInviteResults] = useCompleteInviteMutation({
    onError: () => {
      // error handled in the boundary
    },
  })
  const [Form, { formState, trigger }] = useForm<ActivateUserFormNewValues>({
    schema,
    values: {
      firstName: firstName ?? '',
      lastName: lastName ?? '',
      email: email ?? '',
      password: '',
    },
  })

  const handlePasswordChange = useCallback(() => {
    // Trigger validation for password on every change
    void trigger('password')
  }, [trigger])

  const handleSubmit = useCallback(
    async (values: ActivateUserFormNewValues) => {
      const { data } = await completeInvite({
        variables: {
          input: {
            ...values,
            stateToken: stateToken,
          },
        },
      })

      if (data) {
        await onSubmit(data.completeInvite)
      }
    },
    [completeInvite, onSubmit, stateToken],
  )

  const handleRetry = useCallback(
    () => completeInviteResults.reset(),
    [completeInviteResults],
  )

  return (
    <GraphQLErrorBoundary
      error={completeInviteResults.error}
      retry={handleRetry}
    >
      <Form className="mt-6 text-left" onSubmit={handleSubmit}>
        <h4 className="text-center type-h-title-md">{email}</h4>
        <div className="mt-6 flex flex-col gap-4 lg:flex-row">
          <Form.Input
            className="w-full"
            data-testid="activate-user-first-name"
            label={t`form.firstName`}
            name="firstName"
            placeholder={t`form.firstName`}
          />
          <Form.Input
            className="w-full"
            data-testid="activate-user-last-name"
            label={t`form.lastName`}
            name="lastName"
            placeholder={t`form.lastName`}
          />
        </div>
        <Form.PasswordInput
          aria-describedby="validation-list"
          className="mt-4 w-full"
          data-testid="input-password"
          label={t`form.password.label`}
          name="password"
          onChange={handlePasswordChange}
          placeholder={t`form.password.label`}
          hideError
        />
        <UsersForms.Input.PasswordValidationList
          disabled={formState.isSubmitting}
          errors={formState.errors.password}
          id="validation-list"
          shouldShowValidation={Boolean(formState.dirtyFields.password)}
        />
        <Form.SubmitButton className="mt-6 w-full" variant="primary">
          {t`common:continue`}
        </Form.SubmitButton>
      </Form>
    </GraphQLErrorBoundary>
  )
}
